import React, { useState } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { connect, DispatchProp, useSelector, useDispatch } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { CRow, CCol, CLink, CForm } from '@coreui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LogoUpload from './LogoUpload';
import { RootState } from '../../store/state';
import ColorsPickerForm from './ColorsPickerForm';
import LabelsForm from './LabelsForm';
import ConfigVersioning from './ConfigVersioning';
import ProofOfDelivery, { PROOF_OF_DELIVERY_OPTIONS } from './ProofOfDelivery';
import VehicleIconForm, { VehicleImageType } from './VehicleIconForm';
import ChatForm from './ChatForm';
import BulkAgentsRemovalForm from './BulkAgentsRemovalForm';
import OrgLocationIconForm from './OrgLocationIconForm';
import ReviewLink, { REVIEW_LINK_TYPES } from './ReviewLink';
import LoadingButton from '../../shared/components/LoadingButton';
import { selectJourneyConfig } from '../../store/selectors/journey.selectors';
import { UpdateConfig, UploadVehicleImage } from '../actions/journey';
import {
  JOURNEY_AD_CAROUSEL_ENABLE,
  JOURNEY_AD_CAROUSEL_ADS,
  JOURNEY_AD_CAROUSEL_LOCALES,
  JOURNEY_APP_PRIMARY_COLOR,
  JOURNEY_APP_SECONDARY_COLOR,
  JOURNEY_CHAT_ENABLE,
  JOURNEY_CHAT_PHASES,
  JOURNEY_CONFIRMATION_LOCALES,
  JOURNEY_CONFIRMATION_PHOTO_LABEL,
  JOURNEY_DESTINATION_ICON_TYPE,
  JOURNEY_DESTINATION_ICON_URL,
  JOURNEY_FEEDBACK_REVIEW_LINK,
  JOURNEY_FEEDBACK_REVIEW_LINK_TITLE,
  JOURNEY_PHASES,
  ORGANIZATION_CHAT_ENABLE,
  ORGANIZATION_PROOF_OF_DELIVERY,
} from '../constants';
import { UpdateOrgConfig } from '../actions/organization';
import { Organization, OrganizationConfig } from '../../shared/models/organization';
import { AdWidget, AdWidgetLocale, ImageFile, JobPhases, JourneyConfig } from '../../shared/models/journey';
import { BULK_AGENTS_REMOVAL_TAG } from '../../shared/constants/tags';
import { EditOrganization } from '../../store/actions/orgHierarchy.action';
import { CustomizationFormProps } from '../types';
import updateLocales from '../../shared/utils/update-locales-utils';
import AdsCarouselForm from './AdsCarouselForm';
import JourneyService from '../services/journey.service';

const ORG_CONFIG_VALUE_MAP = {
  none: {
    signature: false,
    photo: false,
  },
  signature: {
    signature: true,
    photo: false,
  },
  photo: {
    signature: false,
    photo: true,
  },
  both: {
    signature: true,
    photo: true,
  },
};

const colorValidation = Yup.string().trim().matches(/^#[0-9A-F]{6}$/i, 'The color should be in HEX format: #XXXXXX').required(' This field is required');

const validationSchema = Yup.object({
  primary: colorValidation,
  secondary: colorValidation,
  chatPhasesEnabled: Yup.bool(),
  bulkAgentsRemoveEnabled: Yup.bool(),
  chatPhases: Yup.array().of(
    Yup.string(),
  ),
  capture: Yup.string()
    .oneOf(PROOF_OF_DELIVERY_OPTIONS.map(option => option.value))
    .required('Capture type is required'),
  confirmationPhotoLabel: Yup.string(),
  agent: Yup.string().required('Agent label is required'),
  task: Yup.string().required('Task label is required'),
  reviewLink: Yup.string(),
  reviewLinkTitle: Yup.string().oneOf(REVIEW_LINK_TYPES.map(option => option.value)),
});


const getProofOfDeliveryCaptureType = (config: { signature?: boolean; photo?: boolean; }) => {
  if (config?.signature && config?.photo) return 'both';
  if (!config?.signature && !config?.photo) return 'none';
  if (config?.signature) return 'signature';
  return 'photo';
};

function mergeArraysByIndex<T, U>(array1: T[] = [], array2: U[] = []): (T | U)[] {
  const maxLength = Math.max(array1.length, array2.length);
  const mergedArray: (T | U)[] = [];

  for (let i = 0; i < maxLength; i += 1) {
    mergedArray.push({ ...array1[i], ...array2[i] });
  }

  return mergedArray;
}

function updatePhasesAdCarouselValue(phases: JobPhases, value: boolean): JobPhases {
  return Object.fromEntries(
    Object.entries(phases).map(([key, phase]) => [
      key,
      {
        ...phase,
        enabled: value,
        carouselAdsEnabled: value
      }
    ])
  ) as JobPhases;
}

export interface CustomizationPageProps extends DispatchProp {
  configID: string;
  org: Organization;
  orgConfig: OrganizationConfig;
  journeyConfig: JourneyConfig;
  saving: boolean;
}

const CustomizationPage = ({ configID, org, orgConfig, journeyConfig, saving }: CustomizationPageProps) => {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const {
    enableChatModule,
  } = useFlags();
  const config = useSelector(selectJourneyConfig);
  const [vehicleIconData, setVehicleIconData] = useState(null);

  const isVehicleTypeWasChanged = journeyConfig?.theme?.agentIcon?.vehicleType !== vehicleIconData?.vehicleType;

  const handleChangeVehicleIcon = (vehicleIcon: File, logoIcon: File, vehicleLogoPreviewImage: string, vehicleType: VehicleImageType) => {
    setVehicleIconData({ vehicleIcon, logoIcon, vehicleLogoPreviewImage, vehicleType });
  };

  const handleSubmit = (values: CustomizationFormProps) => {
    // eslint-disable-next-line no-use-before-define
    if (formik.dirty) {
      const updatedLabels = updateLocales({ agent: values.agent, task: values.task }, config);
      const updatedAdCarousel = values.adCarouselWidget.map(({  href, img_alt, src, target }: AdWidget) => ({ href, img_alt, src, target }));
      const updatedAdCarouselLocales = values.adCarouselWidget.map(({ title, description, altText, linkLabel }: AdWidgetLocale) => ({ title, description, altText, linkLabel }))

      dispatch(new UpdateConfig(org?._id, [
        {
          path: JOURNEY_APP_PRIMARY_COLOR,
          value: values.primary,
        },
        {
          path: JOURNEY_APP_SECONDARY_COLOR,
          value: values.secondary,
        },
        {
          path: JOURNEY_DESTINATION_ICON_TYPE,
          value: 'custom',
        },
        {
          path: JOURNEY_DESTINATION_ICON_URL,
          value: null,
        },
        {
          path: JOURNEY_CHAT_ENABLE,
          value: values?.chatPhasesEnabled ?? false,
        }, {
          path: JOURNEY_CHAT_PHASES,
          value: values?.chatPhases ?? [],
        },
        {
          path: JOURNEY_FEEDBACK_REVIEW_LINK,
          value: values.reviewLink,
        },
        {
          path: JOURNEY_FEEDBACK_REVIEW_LINK_TITLE,
          value: values.reviewLinkTitle,
        },
        {
          path: JOURNEY_CONFIRMATION_LOCALES,
          value: updatedLabels,
        },
        {
          path: JOURNEY_CONFIRMATION_PHOTO_LABEL,
          value: values.confirmationPhotoLabel,
        },
        {
          path: JOURNEY_AD_CAROUSEL_ENABLE,
          value: values.adCarouselEnabled,
        },
        {
          path: JOURNEY_AD_CAROUSEL_ADS,
          value: updatedAdCarousel,
        },
        {
          path: JOURNEY_AD_CAROUSEL_LOCALES,
          value: updatedAdCarouselLocales,
        },
        {
          path: JOURNEY_PHASES,
          value: updatePhasesAdCarouselValue(config.phases, values.adCarouselEnabled),
        },
      ]));

      const enabledChatPhasesUpdates = values?.chatPhases?.reduce((acc, curr) => {
        acc[curr] = {
          ...(orgConfig?.phases?.types?.[curr] ?? {}),
          chat_enabled: values?.chatPhasesEnabled ?? false,
        };
        return acc;
      }, {});

      dispatch(new UpdateOrgConfig([{
        path: ORGANIZATION_CHAT_ENABLE,
        value: {
          ...orgConfig?.phases?.types,
          ...enabledChatPhasesUpdates,
        },
      },
        {
          path: ORGANIZATION_PROOF_OF_DELIVERY,
          value: {
            ...orgConfig?.tasks?.image_upload,
            ...ORG_CONFIG_VALUE_MAP[values.capture] || ORG_CONFIG_VALUE_MAP.none,
          },
        },
      ]));

      // eslint-disable-next-line no-use-before-define
      if (isBulkAgentsRemoveEnabledDirty()) {
        const tags = [...(org?.tags ?? [])];

        if (values?.bulkAgentsRemoveEnabled && tags?.indexOf(BULK_AGENTS_REMOVAL_TAG) === -1) {
          tags.push(BULK_AGENTS_REMOVAL_TAG);
        } else if (!values?.bulkAgentsRemoveEnabled) {
          const index = tags.indexOf(BULK_AGENTS_REMOVAL_TAG);
          tags.splice(index, 1);
        }
        dispatch(new EditOrganization({
          orgId: org?._id,
          updates: {
            tags,
          },
        }));
      }
    }

    if (vehicleIconData.logoIcon || vehicleIconData.vehicleIcon || isVehicleTypeWasChanged) {
      dispatch(new UploadVehicleImage(org._id, vehicleIconData.vehicleIcon, vehicleIconData.logoIcon, vehicleIconData.vehicleLogoPreviewImage, vehicleIconData.vehicleType));
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      adCarouselWidget: mergeArraysByIndex(config?.widgets?.adCarouselWidget?.ads, config?.locales?.en?.adCarouselWidget),
      bulkAgentsRemoveEnabled: org?.tags?.indexOf(BULK_AGENTS_REMOVAL_TAG) !== -1,
      primary: config?.theme?.appPrimaryColor || '',
      secondary: config?.theme?.appSecondaryColor || '',
      chatPhasesEnabled: config?.generalConfiguration.chatWidgetEnabled,
      chatPhases: config?.widgets?.chatWidget?.phases,
      adCarouselEnabled: config?.widgets?.adCarouselWidget?.enabled,
      capture: getProofOfDeliveryCaptureType(orgConfig?.tasks?.image_upload),
      confirmationPhotoLabel: journeyConfig?.locales?.en?.feedbackWidget?.viewConfirmationPhotoButtonLabel || '',
      agent: config?.locales?.en?.driverWidget?.agentTitle || '',
      task: config?.locales?.en?.orderWidget?.appointmentType || '',
      reviewLink: config?.widgets?.feedbackWidget?.reviewLink || '',
      reviewLinkTitle: config?.widgets?.feedbackWidget?.reviewType || '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleUpdateAdsCarousel = (value: (AdWidget | AdWidgetLocale)[]) => {
    formik.setFieldValue('adCarouselWidget', value)
  };

  const handleUploadAdCarouselImage = async (image: ImageFile) => {
    return JourneyService.uploadImage(org?._id, image as File, 'image')
      .then((imageUrl) =>  imageUrl);
  }

  function isBulkAgentsRemoveEnabledDirty() {
    const initialValue = org?.tags?.indexOf(BULK_AGENTS_REMOVAL_TAG) !== -1;
    return formik.getFieldMeta('bulkAgentsRemoveEnabled').value !== initialValue;
  }

  return (
    <Switch>
      <Route exact path={match.url}>
        <>
          <CForm onSubmit={formik.handleSubmit}>
            <FormikProvider value={formik}>
              <CCol lg="12">
                <div id="customize-page">
                  <CRow className="d-flex justify-content-end flex-row px-4 py-2" style={{ gap: 8 }}>
                    <CLink
                      href={`${process.env.PREVIEW_URL}/demobot?journey=${configID}&${org.referrer_org_id === parseInt(process.env.ORGANIZATION_SIGNUP_REFERRER_ID_S1, 10) ? 'shard=1': ''}`}
                      disabled={!configID}
                      className="btn btn-info bg-color-orange roundedBtn"
                      target="_blank"
                    >
                      Preview
                    </CLink>

                    <LoadingButton
                      className="bg-color-orange roundedBtn roundBottomShadow"
                      type="submit"
                      color="info"
                      loadingLabel="Saving..."
                      isLoading={saving}
                      disabled={!formik.dirty && !vehicleIconData?.logoIcon && !vehicleIconData?.vehicleIcon && !isVehicleTypeWasChanged|| saving}
                    >
                      Save
                    </LoadingButton>
                  </CRow>
                  <CRow>
                    <CCol lg="12">
                      <LogoUpload />
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol lg="6">
                      <LabelsForm />
                      <ProofOfDelivery />
                      {enableChatModule ? <ChatForm /> : null}
                      <ReviewLink />
                    </CCol>
                    <CCol lg="6">
                      <ColorsPickerForm />
                      <VehicleIconForm handleChangeVehicleIcon={handleChangeVehicleIcon} />
                      <OrgLocationIconForm />
                      <ConfigVersioning />
                      <BulkAgentsRemovalForm />
                    </CCol>
                    <CCol>
                      <AdsCarouselForm
                        value={formik.getFieldProps('adCarouselWidget').value}
                        handleUpdate={handleUpdateAdsCarousel}
                        handleUploadImage={handleUploadAdCarouselImage}
                      />
                    </CCol>
                  </CRow>
                </div>
              </CCol>
            </FormikProvider>
          </CForm>
        </>
      </Route>
    </Switch>
  );
};

const mapStateToProps = (state: RootState) => ({
  configID: state?.journey?.config?.configID,
  org: state?.orgHierarchy?.selectedOrg,
  orgConfig: state?.orgHierarchy?.selectedOrg?.config,
  journeyConfig: state?.journey?.config?.data,
  saving: state?.journey?.config?.saving,
});

export default connect(mapStateToProps)(CustomizationPage);
